import React, { FC, useState, useEffect, useRef } from "react";
import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { getUrlParts, useBackgroundColor } from "../../utils";
import Typography from "../typography/Typography";
import Stack from "../stack/Stack";
import { Button } from "../button";
import { Close } from "../icon";
import Link from "../link/Link";
import { ThemeButtonKeys } from "../../styles/tokens/buttons";
import { ThemeColorsKeys } from "../../styles/tokens/colors";
import Portal from "../Portal";
import { useDataLayerPush } from "../../hooks/useDataLayerPush";

export interface BladeProps {
  heading?: string;
  subHeading?: string;
  children?: React.ReactNode;
  buttonVariant?: ThemeButtonKeys | "link";
  buttonLabel?: string;
  backgroundColor?: ThemeColorsKeys;
  hideArrow?: boolean;
}

export interface StyledBladeProps {
  isOpen?: boolean;
  isAnimating?: boolean;
  isExiting?: boolean;
  backgroundColor: ThemeColorsKeys;
}

export interface StyledOverlayProps {
  isAnimating?: boolean;
  isExiting?: boolean;
}

const StyledBlade = styled.div<StyledBladeProps>`
  ${({
    theme: { space, colors, media, layers },
    isOpen,
    isAnimating,
    isExiting,
    backgroundColor,
  }) => css`
    position: fixed;
    transition: top 0.5s ease-in-out;
    top: ${isAnimating ? "0" : "calc(100vh + 1px)"};
    right: 0;
    width: 100%;
    overflow: auto;
    ${useBackgroundColor(backgroundColor)}
    border: solid 1px currentColor;
    z-index: ${layers.blade};
    margin-top: 0;

    @supports not (height: 100dvh) {
      height: 100vh;
      padding: ${space.large} ${space.fluidSmallToLarge} ${space.xxxLarge}
        ${space.fluidSmallToLarge};
    }

    @supports (height: 100dvh) {
      height: 100dvh;
      padding: ${space.large} ${space.fluidSmallToLarge};
    }

    ${isExiting &&
    css`
      top: calc(100vh + 1px);
    `}

    @media (min-width: ${media.medium}) {
      width: 635px;
      transition: right 0.5s ease-in-out;
      right: ${isAnimating ? "0" : "-635px"};
      top: 0;

      ${isExiting &&
      css`
        right: -635px;
      `}
    }
  `};
`;

const StyledHeader = styled(Stack)`
  ${({ theme: { space, borderRadii } }) => css``};
`;

const StyledHeading = styled.div`
  ${({ theme: { space, borderRadii } }) => css`
    display: flex;
    gap: ${space.small};
    align-items: flex-start;
    justify-content: space-between;
  `};
`;

const StyledSubHeading = styled(Typography)`
  ${({ theme: { space, borderRadii } }) => css`
    max-width: 400px;
  `};
`;

const StyledCloseButton = styled(Button)`
  ${({ theme: { space, borderRadii } }) => css`
    width: 100%;
  `};
`;

const StyledButton = styled.button`
  ${({ theme: { space, borderRadii, maxMedia } }) => css`
    display: flex;
    align-items: center;
    gap: ${space.xSmall};

    @media (max-width: ${maxMedia.medium}) {
      span:first-of-type {
        display: none;
      }
    }
  `};
`;

const StyledCloseTopButton = styled(Button)`
  ${({ theme: { space, borderRadii } }) => css`
    width: 48px;
    padding: 0;
  `};
`;

const StyledOverlay = styled.span<StyledOverlayProps>`
  ${({
    theme: { space, colors, media, layers },
    isAnimating,
    isExiting,
  }) => css`
    display: none;
    @media (min-width: ${media.large}) {
      display: block;
      transition: all 0.5s ease-in-out;
      opacity: ${isAnimating ? "0.5" : "0"};
      background: ${colors.darkBlue};
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100vh;
      z-index: ${layers.overlay};
      margin: 0%;

      ${isExiting &&
      css`
        opacity: 0;
      `}
    }
  `};
`;

export const Blade: FC<BladeProps> = ({
  heading,
  subHeading,
  children,
  buttonVariant,
  buttonLabel,
  backgroundColor = "yellow",
  hideArrow,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const BladeRef = useRef<HTMLDivElement>(null);
  const { dataLayerPush } = useDataLayerPush();

 

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }

    // Cleanup function to run when component unmounts
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isOpen]);


  // Handle open enter animation

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;

    if (isOpen) {
      timeoutId = setTimeout(() => {
        setIsAnimating(true);
      }, 50);

      if (closeButtonRef.current) {
        closeButtonRef.current.focus();
      }
    } else {
      clearTimeout(timeoutId!);
      setIsAnimating(false);
    }

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [isOpen]);

  // Handle keyboard navigation

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      if (event.key === "Tab") {
        const focusableElements = Array.from(
          BladeRef.current?.querySelectorAll(
            "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
          ) ?? []
        );

        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0] as HTMLElement;
        const lastElement = focusableElements[
          focusableElements.length - 1
        ] as HTMLElement;

        if (document.activeElement === lastElement && !event.shiftKey) {
          firstElement.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && isOpen) {
        handleClose();
        if (linkRef.current) {
          linkRef.current.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  // Handle clicking outside to close

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        BladeRef.current &&
        !BladeRef.current.contains(event.target as Node) &&
        linkRef.current &&
        !linkRef.current.contains(event.target as Node)
      ) {
        if (isOpen) {
          handleClose();
          setTimeout(() => {
            const linkElement = linkRef.current;
            if (linkElement instanceof HTMLElement) {
              linkElement.focus();
            }
          }, 100);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Handle open
  const handleOpen = () => {
    dataLayerPush({
      event: "blade_open",
      name: heading,
    });

    setIsOpen(true);

    dataLayerPush({
      event: "button_click",
      clickText: "close",
      clickContext: "Blade - close top",
      ...getUrlParts(undefined),
    });
  };

  // Handle close

  const handleClose = () => {
    dataLayerPush({
      event: "blade_closed",
      name: heading,
    });

    let timeoutId: NodeJS.Timeout | null = null;
    setIsExiting(true);
    timeoutId = setTimeout(() => {
      setIsOpen(false);
      setIsExiting(false);
      if (linkRef.current) {
        linkRef.current.focus();
      }
    }, 500);
  };

  return (
    <>
      {isOpen && (
        <Portal id="blade-portal">
          <StyledOverlay isAnimating={isAnimating} isExiting={isExiting} />
          <StyledBlade
            isAnimating={isAnimating}
            isExiting={isExiting}
            ref={BladeRef}
            role="dialog"
            aria-modal="true"
            id="BladeOverlay"
            title={heading}
            backgroundColor={backgroundColor}
          >
            <Stack gap="fluidSmallToMedium">
              <StyledHeader gap="small">
                <StyledHeading>
                  <Typography component="h2" variant="headingMedium">
                    {heading}
                  </Typography>
                  <StyledButton
                    onClick={handleClose}
                    type="button"
                    title="Close"
                    ref={closeButtonRef}
                  >
                    <Typography component="span" variant="bodyBold">
                      Close
                    </Typography>
                    <StyledCloseTopButton
                      variant="neutral"
                      iconLeft={<Close size="small" />}
                      size="small"
                      as="span"
                    />
                  </StyledButton>
                </StyledHeading>
                {subHeading && (
                  <StyledSubHeading
                    component="p"
                    variant="secondaryHeadingSmall"
                  >
                    {subHeading}
                  </StyledSubHeading>
                )}
              </StyledHeader>

              <div>{children}</div>
              <StyledCloseButton
                variant="neutral"
                label="Close"
                iconLeft={<Close size="small" />}
                onClick={handleClose}
                size="small"
                dataLayerClickContext="Blade - Close bottom"
              />
            </Stack>
          </StyledBlade>
        </Portal>
      )}

      {buttonVariant === "link" ? (
        <Link
          label={buttonLabel}
          onClick={handleOpen}
          ref={linkRef}
          aria-haspopup="dialog"
          aria-expanded={isOpen ? "true" : "false"}
          aria-controls="BladeOverlay"
          dataLayerClickContext="Blade - Open"
          hideArrow={hideArrow}
        />
      ) : (
        <Button
          variant={buttonVariant}
          label={buttonLabel}
          onClick={handleOpen}
          ref={linkRef}
          aria-haspopup="dialog"
          aria-expanded={isOpen ? "true" : "false"}
          aria-controls="BladeOverlay"
          arrowRight
          dataLayerClickContext="Blade - Open"
        />
      )}
    </>
  );
};

export default Blade;
